import React, { useState, useEffect } from 'react';
import { getAgeRating, updateAgeRating, updateAgeRatingByVersion } from '../../services/api';

function AgeRating({ versionId, onUpdate }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [ageRatingId, setAgeRatingId] = useState(null);
  const [ratings, setRatings] = useState({
    // Content rating level fields (NONE, INFREQUENT_OR_MILD, FREQUENT_OR_INTENSE)
    alcoholTobaccoOrDrugUseOrReferences: 'NONE',
    contests: 'NONE',
    gamblingSimulated: 'NONE',
    gunsOrOtherWeapons: 'NONE',
    horrorOrFearThemes: 'NONE',
    matureOrSuggestiveThemes: 'NONE',
    medicalOrTreatmentInformation: 'NONE',
    profanityOrCrudeHumor: 'NONE',
    sexualContentGraphicAndNudity: 'NONE',
    sexualContentOrNudity: 'NONE',
    violenceCartoonOrFantasy: 'NONE',
    violenceRealistic: 'NONE',
    violenceRealisticProlongedGraphicOrSadistic: 'NONE',
    
    // Boolean content fields (true/false)
    advertising: false,
    ageAssurance: false,
    gambling: false,
    healthOrWellnessTopics: false,
    lootBox: false,
    messagingAndChat: false,
    parentalControls: false,
    unrestrictedWebAccess: false,
    userGeneratedContent: false,
    
    // Special fields
    kidsAgeBand: null, // FIVE_AND_UNDER, SIX_TO_EIGHT, NINE_TO_ELEVEN, or null
    developerAgeRatingInfoUrl: ''
  });

  useEffect(() => {
    if (versionId) {
      loadAgeRating();
    }
  }, [versionId]);

  const loadAgeRating = async () => {
    setLoading(true);
    try {
      const response = await getAgeRating(versionId);
      console.log('Age rating response:', response.data);
      
      if (response.data.success && response.data.ageRating) {
        setAgeRatingId(response.data.ageRating.id);
        
        // Merge the attributes with default values
        const attributes = response.data.ageRating.attributes || {};
        setRatings(prevRatings => ({
          ...prevRatings,
          ...attributes,
          // Ensure null kidsAgeBand is handled properly
          kidsAgeBand: attributes.kidsAgeBand || null,
          // Ensure URL field is properly handled
          developerAgeRatingInfoUrl: attributes.developerAgeRatingInfoUrl || ''
        }));
      } else {
        // If we can't load existing age rating, we might still be able to create/update one
        // For now, we'll show a warning but still allow the user to try saving
        console.warn('Could not load existing age rating:', response.data.error);
        
        // Set a temporary ID to allow saving - the server will handle finding the correct ID
        setAgeRatingId('temp-id-for-creation');
      }
    } catch (err) {
      console.error('Failed to load age rating:', err);
      
      // Even if loading fails, allow the user to try creating/updating
      setAgeRatingId('temp-id-for-creation');
      
      // Show a warning but don't block the user
      console.warn('Age rating loading failed, but you can still try to update the age rating declaration.');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!versionId) {
      alert('Version ID not available. Please try refreshing the page.');
      return;
    }

    setSaving(true);
    try {
      let response;
      
      // If we have a valid age rating ID, use the direct update method
      if (ageRatingId && ageRatingId !== 'temp-id-for-creation') {
        response = await updateAgeRating(ageRatingId, ratings);
      } else {
        // Otherwise, use the version-based update method
        response = await updateAgeRatingByVersion(versionId, ratings);
      }
      
      console.log('Age rating update response:', response.data);
      
      if (response.data.success) {
        alert('Age rating declaration updated successfully! Apple will assign the appropriate age rating based on your responses.');
        
        // Update the age rating ID with the returned data
        if (response.data.ageRating && response.data.ageRating.id) {
          setAgeRatingId(response.data.ageRating.id);
        }
        
        if (onUpdate) {
          onUpdate();
        }
      } else {
        throw new Error(response.data.error || 'Update failed');
      }
    } catch (err) {
      console.error('Failed to update age rating:', err);
      const errorMessage = err.response?.data?.error || err.message || 'Unknown error occurred';
      
      // Show more user-friendly error messages
      if (errorMessage.includes('validation')) {
        alert('Please check your inputs. Some values may be invalid.');
      } else if (errorMessage.includes('authorization') || errorMessage.includes('unauthorized')) {
        alert('Authentication error. Please log in again.');
      } else if (errorMessage.includes('not found')) {
        alert('Age rating declaration not found. This may be a new app version that needs to be set up.');
      } else {
        alert('Failed to update age rating declaration: ' + errorMessage);
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="loading-inline">
        <div className="spinner"></div>
      </div>
    );
  }

  const levelOptions = [
    { value: 'NONE', label: 'None' },
    { value: 'INFREQUENT_OR_MILD', label: 'Infrequent/Mild' },
    { value: 'FREQUENT_OR_INTENSE', label: 'Frequent/Intense' }
  ];

  const kidsAgeBandOptions = [
    { value: null, label: 'Not applicable' },
    { value: 'FIVE_AND_UNDER', label: '5 and Under' },
    { value: 'SIX_TO_EIGHT', label: '6 to 8' },
    { value: 'NINE_TO_ELEVEN', label: '9 to 11' }
  ];

  // Rating level categories (NONE, INFREQUENT_OR_MILD, FREQUENT_OR_INTENSE)
  const contentRatingCategories = [
    {
      key: 'alcoholTobaccoOrDrugUseOrReferences',
      label: 'Alcohol, Tobacco, or Drug Use or References',
      description: 'References to or consumption of alcohol, tobacco, or drugs'
    },
    {
      key: 'contests',
      label: 'Contests',
      description: 'Contests or competitions (12+ rating if frequent/intense)'
    },
    {
      key: 'gamblingSimulated',
      label: 'Simulated Gambling',
      description: 'Gambling themes or simulated casino games'
    },
    {
      key: 'gunsOrOtherWeapons',
      label: 'Guns or Other Weapons',
      description: 'References to, depictions of, or use of weapons'
    },
    {
      key: 'horrorOrFearThemes',
      label: 'Horror or Fear Themes',
      description: 'Scary or disturbing content intended to frighten'
    },
    {
      key: 'matureOrSuggestiveThemes',
      label: 'Mature or Suggestive Themes',
      description: 'Adult themes or suggestive content'
    },
    {
      key: 'medicalOrTreatmentInformation',
      label: 'Medical or Treatment Information',
      description: 'Medical advice or treatment information'
    },
    {
      key: 'profanityOrCrudeHumor',
      label: 'Profanity or Crude Humor',
      description: 'Strong language or crude jokes'
    },
    {
      key: 'sexualContentGraphicAndNudity',
      label: 'Sexual Content - Graphic and Nudity',
      description: 'Explicit sexual content or nudity'
    },
    {
      key: 'sexualContentOrNudity',
      label: 'Sexual Content or Nudity',
      description: 'Sexual themes or partial nudity'
    },
    {
      key: 'violenceCartoonOrFantasy',
      label: 'Cartoon or Fantasy Violence',
      description: 'Non-realistic violence in cartoon style'
    },
    {
      key: 'violenceRealistic',
      label: 'Realistic Violence',
      description: 'Realistic depictions of violence'
    },
    {
      key: 'violenceRealisticProlongedGraphicOrSadistic',
      label: 'Realistic Violence - Prolonged, Graphic, or Sadistic',
      description: 'Intense realistic violence with graphic detail'
    }
  ];

  const booleanCategories = [
    {
      key: 'advertising',
      label: 'Advertising',
      description: 'Contains advertising content'
    },
    {
      key: 'ageAssurance',
      label: 'Age Assurance',
      description: 'Age verification is required'
    },
    {
      key: 'gambling',
      label: 'Gambling',
      description: 'Real money gambling (17+ rating if enabled)'
    },
    {
      key: 'healthOrWellnessTopics',
      label: 'Health or Wellness Topics',
      description: 'Health-related content or wellness information'
    },
    {
      key: 'lootBox',
      label: 'Loot Box',
      description: 'Random item rewards that can be purchased'
    },
    {
      key: 'messagingAndChat',
      label: 'Messaging and Chat',
      description: 'User communication features'
    },
    {
      key: 'parentalControls',
      label: 'Parental Controls',
      description: 'Includes parental control features'
    },
    {
      key: 'unrestrictedWebAccess',
      label: 'Unrestricted Web Access',
      description: 'Allows unrestricted web browsing'
    },
    {
      key: 'userGeneratedContent',
      label: 'User Generated Content',
      description: 'Content created by users'
    }
  ];

  return (
    <div>
      <h2>Age Rating Declaration</h2>

      <div style={{ marginBottom: '2rem' }}>
        <p className="text-secondary" style={{ marginBottom: '1rem' }}>
          Provide content information so Apple can determine your app's age rating. 
          Apple will automatically assign an age rating based on your responses.
        </p>
        <div className="info-card">
          <strong>Note:</strong> Apple determines the final age rating based on these declarations. 
          Be accurate and honest to ensure proper age rating assignment.
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
        {/* Content Rating Categories */}
        <div>
          <h3 style={{ margin: '0 0 1rem 0', fontSize: '18px', fontWeight: '600' }}>
            Content Ratings
          </h3>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', 
            gap: '1rem' 
          }}>
            {contentRatingCategories.map(category => (
              <div key={category.key} className="form-card" style={{ height: '100%' }}>
                <label className="form-label" style={{ marginBottom: '0.5rem', fontSize: '14px' }}>
                  {category.label}
                </label>
                <p className="text-secondary" style={{ fontSize: '12px', marginBottom: '0.75rem', lineHeight: '1.4' }}>
                  {category.description}
                </p>
                <select
                  className="form-select"
                  value={ratings[category.key] || 'NONE'}
                  onChange={(e) => setRatings({ ...ratings, [category.key]: e.target.value })}
                  style={{ width: '100%' }}
                >
                  {levelOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            ))}
          </div>
        </div>

        {/* Boolean Categories */}
        <div>
          <h3 style={{ margin: '0 0 1rem 0', fontSize: '18px', fontWeight: '600' }}>
            Additional Features
          </h3>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', 
            gap: '1rem' 
          }}>
            {booleanCategories.map(category => (
              <div key={category.key} className="form-card" style={{ height: '100%' }}>
                <div className="toggle-container">
                  <label className="toggle">
                    <input
                      type="checkbox"
                      checked={ratings[category.key] || false}
                      onChange={(e) => setRatings({ ...ratings, [category.key]: e.target.checked })}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                  <div style={{ marginLeft: '12px', flex: 1 }}>
                    <span className="form-label" style={{ marginBottom: '0.25rem', display: 'block', fontSize: '14px' }}>
                      {category.label}
                    </span>
                    <p className="text-secondary" style={{ fontSize: '12px', margin: 0, lineHeight: '1.4' }}>
                      {category.description}
                    </p>
                    {category.key === 'gambling' && (
                      <small className="text-primary" style={{ display: 'block', marginTop: '0.25rem', fontSize: '11px' }}>
                        Enabling this will result in a 17+ age rating
                      </small>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Kids Age Band */}
        <div>
          <h3 style={{ margin: '0 0 1rem 0', fontSize: '18px', fontWeight: '600' }}>
            Kids Category (Optional)
          </h3>
          <div className="form-card">
            <label className="form-label" style={{ marginBottom: '0.5rem' }}>
              Kids Age Band
            </label>
            <p className="text-secondary" style={{ fontSize: '13px', marginBottom: '1rem', lineHeight: '1.4' }}>
              If your app is designed for children, select the appropriate age band. 
              This helps categorize your app in the Kids section of the App Store.
            </p>
            <select
              className="form-select"
              value={ratings.kidsAgeBand || ''}
              onChange={(e) => setRatings({ 
                ...ratings, 
                kidsAgeBand: e.target.value === '' ? null : e.target.value 
              })}
              style={{ maxWidth: '200px' }}
            >
              {kidsAgeBandOptions.map(option => (
                <option key={option.value || 'none'} value={option.value || ''}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Developer Age Rating Info URL */}
        <div>
          <h3 style={{ margin: '0 0 1rem 0', fontSize: '18px', fontWeight: '600' }}>
            Additional Information
          </h3>
          <div className="form-card">
            <label className="form-label" style={{ marginBottom: '0.5rem' }}>
              Age Rating Information URL (Optional)
            </label>
            <p className="text-secondary" style={{ fontSize: '13px', marginBottom: '1rem', lineHeight: '1.4' }}>
              Provide a URL with additional information about your app's age rating and content.
            </p>
            <input
              type="url"
              className="form-input"
              value={ratings.developerAgeRatingInfoUrl || ''}
              onChange={(e) => setRatings({ ...ratings, developerAgeRatingInfoUrl: e.target.value })}
              placeholder="https://example.com/age-rating-info"
              style={{ maxWidth: '400px' }}
            />
          </div>
        </div>
      </div>

      <div className="info-card" style={{ marginTop: '2rem' }}>
        <strong>Important:</strong> Apple will review your declarations and assign the appropriate 
        age rating. Inaccurate information may result in app rejection or removal from the App Store.
      </div>

      <button
        className="btn btn-primary"
        onClick={handleSave}
        disabled={saving || !versionId}
        style={{ marginTop: '1.5rem' }}
      >
        {saving ? 'Saving...' : 'Update Age Rating Declaration'}
      </button>
    </div>
  );
}

export default AgeRating;
